import { useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { PAGE_PADDING } from "@/constants";

type PageLayoutConfig = {
  maxWidthPx?: number;
  paddingLeftPx?: number;
  paddingRightPx?: number;
  paddingYPx?: number;
  secondarySidebar?: boolean;
};

/** Per-route layout override for `/domains/:id` only. */
export const DOMAIN_DETAIL_PAGE_LAYOUT: PageLayoutConfig = {
  paddingLeftPx: PAGE_PADDING.DOMAIN_DETAIL_PAGE.LEFT_PX,
  paddingRightPx: PAGE_PADDING.DOMAIN_DETAIL_PAGE.RIGHT_PX,
  paddingYPx: PAGE_PADDING.DOMAIN_DETAIL_PAGE.Y_PX,
  secondarySidebar: true,
};

/** Per-route layout override for `/app-studio/:id`. */
export const APP_BUILD_PAGE_LAYOUT: PageLayoutConfig = {
  paddingLeftPx: PAGE_PADDING.APP_BUILD_PAGE.LEFT_PX,
  paddingRightPx: PAGE_PADDING.APP_BUILD_PAGE.RIGHT_PX,
  paddingYPx: PAGE_PADDING.APP_BUILD_PAGE.Y_PX,
};

/** Per-route layout override for `/` (home page). */
export const HOME_PAGE_LAYOUT: PageLayoutConfig = {
  paddingLeftPx: PAGE_PADDING.HOME_PAGE.LEFT_PX,
  paddingRightPx: PAGE_PADDING.HOME_PAGE.RIGHT_PX,
  paddingYPx: PAGE_PADDING.HOME_PAGE.Y_PX,
};

type RoutePageLayoutEntry = {
  pattern: string;
  layout: PageLayoutConfig;
};

/** Path patterns with layout overrides (first match wins). */
const ROUTE_PAGE_LAYOUTS: RoutePageLayoutEntry[] = [
  { pattern: "/", layout: HOME_PAGE_LAYOUT },
  { pattern: "/domains/:id", layout: DOMAIN_DETAIL_PAGE_LAYOUT },
  { pattern: "/app-studio/:id", layout: APP_BUILD_PAGE_LAYOUT },
];

export type ResolvedPageLayout = {
  maxWidthPx?: number;
  paddingLeftPx: number;
  paddingRightPx: number;
  paddingYPx: number;
  secondarySidebar: boolean;
};

const DEFAULT_PAGE_LAYOUT: ResolvedPageLayout = {
  paddingLeftPx: PAGE_PADDING.DEFAULT.LEFT_PX,
  paddingRightPx: PAGE_PADDING.DEFAULT.RIGHT_PX,
  paddingYPx: PAGE_PADDING.DEFAULT.Y_PX,
  secondarySidebar: false,
};

function applyLayoutOverride(
  base: ResolvedPageLayout,
  override: PageLayoutConfig,
): ResolvedPageLayout {
  return {
    maxWidthPx: override.maxWidthPx ?? base.maxWidthPx,
    paddingLeftPx: override.paddingLeftPx ?? base.paddingLeftPx,
    paddingRightPx: override.paddingRightPx ?? base.paddingRightPx,
    paddingYPx: override.paddingYPx ?? base.paddingYPx,
    secondarySidebar: override.secondarySidebar ?? base.secondarySidebar,
  };
}

export function resolvePageLayoutFromPathname(pathname: string): ResolvedPageLayout {
  for (const { pattern, layout } of ROUTE_PAGE_LAYOUTS) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      return applyLayoutOverride(DEFAULT_PAGE_LAYOUT, layout);
    }
  }

  return DEFAULT_PAGE_LAYOUT;
}

export function usePageLayout(): ResolvedPageLayout {
  const { pathname } = useLocation();
  return useMemo(() => resolvePageLayoutFromPathname(pathname), [pathname]);
}
